import {
  AlertTriangle,
  Bot,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  groupAgentEvents,
  type AgentEventGroup,
  type AgentEventItem,
} from '@/components/agents/helpers/group-agent-events';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { AgentWorkflowEvent } from '@/services/agents/run-events-stream';

interface AgentEventsSidebarProps {
  events: AgentWorkflowEvent[];
  streaming: boolean;
}

export function AgentEventsSidebar({
  events,
  streaming,
}: AgentEventsSidebarProps) {
  const groups = useMemo(() => groupAgentEvents(events), [events]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events.length]);

  return (
    <aside className='w-80 border-l border-border/60 flex flex-col bg-muted/20 shrink-0'>
      <header className='flex items-center gap-2 px-3 py-2.5 border-b border-border/60'>
        {streaming ? (
          <Loader2 className='size-4 animate-spin text-muted-foreground shrink-0' />
        ) : (
          <Bot className='size-4 text-muted-foreground shrink-0' />
        )}
        <h2 className='text-sm font-medium'>Agent activity</h2>
        {groups.length > 0 && (
          <span className='ml-auto text-xs text-muted-foreground'>
            {groups.length} agent{groups.length === 1 ? '' : 's'}
          </span>
        )}
      </header>
      <div ref={scrollRef} className='flex-1 overflow-y-auto p-2 space-y-2'>
        {groups.length === 0 ? (
          <p className='text-xs text-muted-foreground px-2 py-4 text-center'>
            {streaming
              ? 'Waiting for events…'
              : 'Send a message to see live agent activity here.'}
          </p>
        ) : (
          groups.map((group) => (
            <EventGroupSection key={group.key} group={group} />
          ))
        )}
      </div>
    </aside>
  );
}

interface EventGroupSectionProps {
  group: AgentEventGroup;
}

function EventGroupSection({ group }: EventGroupSectionProps) {
  return (
    <Collapsible
      defaultOpen
      className='rounded-md border border-border/60 bg-background/60'
    >
      <CollapsibleTrigger className='group flex w-full items-center gap-1.5 px-2 py-1.5 text-xs text-left hover:bg-accent/40 transition-colors'>
        <ChevronDown className='size-3 shrink-0 text-muted-foreground group-data-[state=closed]:hidden' />
        <ChevronRight className='size-3 shrink-0 text-muted-foreground group-data-[state=open]:hidden' />
        <span className='font-medium truncate'>{group.displayName}</span>
        <span className='ml-auto flex items-center gap-1.5 shrink-0 text-muted-foreground'>
          {group.toolCallCount > 0 && (
            <span className='inline-flex items-center gap-0.5'>
              <Wrench className='size-3' />
              {group.toolCallCount}
            </span>
          )}
          {group.errorCount > 0 && (
            <span className='inline-flex items-center gap-0.5 text-destructive'>
              <AlertTriangle className='size-3' />
              {group.errorCount}
            </span>
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className='border-t border-border/60 px-2 py-1.5 space-y-2'>
          {group.items.map((item) => (
            <EventItemRow key={item.key} item={item} />
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Local time (HH:MM:SS) for an event's ISO timestamp, if parseable. */
export function formatEventTime(iso?: string): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleTimeString(undefined, { hour12: false });
}

const MAX_PAYLOAD_CHARS = 4000;

/**
 * Pretty-prints a JSON payload for the feed: small documents stay on one
 * line, larger ones are indented; non-JSON payloads pass through as-is.
 * Output is capped so a single huge tool result can't swamp the panel.
 */
export function formatPayload(raw: string): string {
  const trimmed = raw.trim();
  let formatted = trimmed;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    const compact = JSON.stringify(parsed);
    formatted =
      compact.length <= 80 ? compact : JSON.stringify(parsed, null, 2);
  } catch {
    // Not JSON — keep the raw text.
  }
  return formatted.length > MAX_PAYLOAD_CHARS
    ? `${formatted.slice(0, MAX_PAYLOAD_CHARS)}\n… (truncated)`
    : formatted;
}

function lineCount(text: string): number {
  let lines = 1;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '\n') lines += 1;
  }
  return lines;
}

interface ItemTimeProps {
  at?: string;
  className?: string;
}

function ItemTime({ at, className }: ItemTimeProps) {
  const time = formatEventTime(at);
  if (!time) return null;
  return (
    <span className={cn('ml-auto shrink-0 tabular-nums', className)}>
      {time}
    </span>
  );
}

interface ExpandToggleProps {
  expanded: boolean;
  onToggle: () => void;
}

function ExpandToggle({ expanded, onToggle }: ExpandToggleProps) {
  return (
    <button
      type='button'
      className='mt-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors'
      onClick={onToggle}
    >
      {expanded ? 'Show less' : 'Show more'}
    </button>
  );
}

const TEXT_CLAMP_CHARS = 320;
const TEXT_CLAMP_LINES = 4;

interface ClampedTextProps {
  text: string;
  className?: string;
}

/** Prose body clamped to a few lines with an expand control when long. */
function ClampedText({ text, className }: ClampedTextProps) {
  const [expanded, setExpanded] = useState(false);
  const clampable =
    text.length > TEXT_CLAMP_CHARS || lineCount(text) > TEXT_CLAMP_LINES;
  return (
    <>
      <p
        className={cn(
          'whitespace-pre-wrap break-words',
          clampable && !expanded && 'line-clamp-4',
          className,
        )}
      >
        {text}
      </p>
      {clampable && (
        <ExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </>
  );
}

const PAYLOAD_CLAMP_CHARS = 240;
const PAYLOAD_CLAMP_LINES = 3;

interface PayloadBlockProps {
  raw: string;
}

/**
 * Monospace payload block: pretty-printed JSON wrapped on word/JSON
 * boundaries. Long payloads are clamped to a preview with an expand
 * control; when expanded, unbreakable tokens scroll horizontally instead
 * of shattering across lines.
 */
function PayloadBlock({ raw }: PayloadBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const formatted = useMemo(() => formatPayload(raw), [raw]);
  if (!formatted) return null;
  const clampable =
    formatted.length > PAYLOAD_CLAMP_CHARS ||
    lineCount(formatted) > PAYLOAD_CLAMP_LINES;
  return (
    <div className='mt-1 min-w-0'>
      <pre
        className={cn(
          'rounded-sm bg-muted px-1.5 py-1 font-mono text-[10px] whitespace-pre-wrap',
          expanded
            ? 'max-h-64 overflow-auto'
            : clampable && 'line-clamp-3 overflow-hidden',
        )}
      >
        {formatted}
      </pre>
      {clampable && (
        <ExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </div>
  );
}

interface EventItemRowProps {
  item: AgentEventItem;
}

function EventItemRow({ item }: EventItemRowProps) {
  if (item.kind === 'thinking' || item.kind === 'message') {
    return (
      <li className='text-xs'>
        <div className='flex items-baseline gap-1.5 text-[10px] text-muted-foreground/70'>
          <span className='font-medium'>
            {item.kind === 'thinking' ? 'Thinking' : 'Message'}
          </span>
          <ItemTime at={item.at} />
        </div>
        <ClampedText
          text={item.text}
          className={
            item.kind === 'thinking'
              ? 'italic text-muted-foreground'
              : 'text-foreground/80'
          }
        />
      </li>
    );
  }

  if (item.kind === 'error') {
    return (
      <li className='rounded-sm border border-destructive/40 bg-destructive/10 px-1.5 py-1 text-xs text-destructive'>
        <span className='flex items-start gap-1'>
          <AlertTriangle className='size-3 mt-0.5 shrink-0' />
          <span className='min-w-0 flex-1 break-words'>{item.text}</span>
          <ItemTime at={item.at} className='text-[10px] text-destructive/70' />
        </span>
        {item.detail && (
          <p className='mt-0.5 pl-4 text-[10px] text-destructive/80 break-words'>
            {item.detail}
          </p>
        )}
      </li>
    );
  }

  return (
    <li className='text-xs'>
      <div className='flex items-center gap-1 text-muted-foreground'>
        <Wrench className='size-3 shrink-0' />
        <span className='min-w-0 truncate font-medium text-foreground/80'>
          {item.toolName}
        </span>
        {item.kind === 'tool_result' && <span className='shrink-0'>→</span>}
        <ItemTime at={item.at} className='text-[10px]' />
      </div>
      {item.detail && <PayloadBlock raw={item.detail} />}
    </li>
  );
}
